// live/table.ts (TB2) - the bridge between TB1's TableSession engine and the live UI.
//
// This is the file that makes "next hand" mean the SAME table. Before TB2 every hand was an independent
// deal: `useLiveDeal` called `dealPlayableHand`, which built a throwaway 3-handed `baseSpec` with hero
// pinned to the button and everyone re-stacked to 150bb. Nothing carried, because nothing was allowed to
// live long enough to carry - `useLiveDeal` sits inside the surface, and the surface is remounted
// (`key={dealNonce}`) and wiped on every New-hand and every stage switch.
//
// So the session is held ABOVE the surfaces, in App state, and this module is the pure core it calls:
// `openTable()` once at sit-down, `nextHand()` per hand. Both are plain functions over an immutable
// TableSession - no hooks, no refs, no module-level mutable state - so they are directly testable and a
// React remount cannot reach them.
//
// WHAT IS DELIBERATELY NOT TOUCHED: `liveRunner.baseSpec` and the `dealPlayableHand` path it feeds. GH4's
// curated hand library and GH7's 24 pre-baked prose entries are keyed by a hash of that exact spec
// (`narrationLibrary.test.ts` fails loudly if the keys move). The session builds its OWN spec via TB1's
// `specForHand`, so the library keeps its hands and its prose. That was the carried note's explicit ask.
import {
  DEFAULT_BUY_IN,
  DEFAULT_STAKES,
  beginHand,
  completeHand,
  createHand,
  newSession,
  seedForHand,
  type BegunHand,
} from '@postflop/engine';
import type {
  ActionKind,
  ArchetypeId,
  HandState,
  SessionEvent,
  Stakes,
  TableSession,
  TableType,
} from '@postflop/schema';
import { applyHeroDecision, dealSessionHand, displayName, type LiveHandModel } from '../engine/liveRunner';
import { markFeltUnread } from '../engine/bridge';
import { ARCHETYPE_TAG } from '../diagnose/data';
import type { TableMode } from '../home/tableMode';
import { STAKES_POOLS, DEFAULT_STAKES_ID } from '../home/stakes';

/**
 * The table you sit down at: 8 villains + hero = 9-handed full ring, the cash default.
 *
 * NINE as of TB3, which is what the North Star's table-size amendment (2026-07-15) always called for. TB2
 * pinned this to 7 for one reason - the felt could not DRAW nine: `RING` held 7 pods and `scenarioToFelt`
 * wrapped with `RING[i % RING.length]`, stacking the 8th villain on the 1st. TB3 added the mirror pod
 * (`bridge.RING_MIRROR`) and replaced the modulo with `ringFor`, so the constraint is gone. The session
 * layer needed no change at all - TB1 built it size-agnostic (`seats.length` is the truth), exactly as
 * planned.
 *
 * CX5 - the roster is no longer a single hardcoded mix: it comes from the chosen STAKES POOL (`home/stakes.ts`),
 * because stakes change the reads (a $1/$3 loose-passive pool vs a $3/$5 reg pool). Every pool roster still
 * leads with the four TAGGABLE archetypes (Vacuum / Firecracker / Rock / Whale), the only ones the read UI can
 * score, so a 6-handed slice keeps all four; the fill after them is the honest pool difference. The default is
 * the $1/$3 pool, so a caller that does not pass a roster gets exactly the prior loose-passive table.
 */
const DEFAULT_ROSTER: readonly ArchetypeId[] = STAKES_POOLS[DEFAULT_STAKES_ID].roster;

/** Supported table sizes (TB0 §9 F1). 9 = full ring, the cash default; 6/7 are selectable at sit-down. */
export const TABLE_SIZES = [6, 7, 9] as const;
export type TableSize = (typeof TABLE_SIZES)[number];
export const DEFAULT_TABLE_SIZE: TableSize = 9;

/** Villains for seats 1..n from a pool roster (taggable-four-first). Hero is always seat 0 (TB1's convention).
 *  Sliced to `size - 1`; the pool rosters are authored 8 long so a 9-handed full ring fills exactly. */
const rosterFor = (
  size: number,
  roster: readonly ArchetypeId[],
): { archetypeId: ArchetypeId; displayName: string }[] =>
  roster.slice(0, size - 1).map((archetypeId) => ({ archetypeId, displayName: displayName(archetypeId) }));

/** The table currently being played: the session (roster, stacks, button, hand number) plus the hand in
 *  front of you. `session` has ALREADY been advanced past `model` - its `buttonSeat`/`handNumber`/stacks
 *  are what the NEXT hand will be dealt with. `hand` is the honest record of the one you are playing. */
export interface TableState {
  /** the session as it stands WHILE this hand is live - hand `model` has NOT been harvested into it yet.
   *  FULL: it used to be the already-advanced session, because the old runner walked the whole hand at deal
   *  time and harvested a terminal nobody had played. A table where hero decides cannot know the result at
   *  deal time, so the harvest moved to `endHand`, where the result is real. */
  session: TableSession;
  model: LiveHandModel;
  /** the hand number of `model`. */
  handNumber: number;
  /** the button `model` was dealt with. */
  buttonSeat: number;
  /** per-seat stacks as they were when `model` was dealt - the honest "what everyone sat down with". */
  startingStacks: number[];
  /** FULL - what `endHand` needs to harvest this hand into the session once hero has finished it. */
  meta: BegunHand['meta'];
}

export interface OpenTableOptions {
  mode: TableMode;
  /** the whole session is a pure function of this - same root, same table, same hands, forever. */
  seedRoot: string;
  sessionId?: string;
  tableSize?: number;
  buyIn?: number;
  /** CX5 - the blinds you sit down at. Drives every real-dollar amount + `stackDepthBB`. Defaults to $1/$3. */
  stakes?: Stakes;
  /** CX5 - the read-the-table (Axis B) ground-truth label for this pool. Defaults to the $1/$3 pool's type. */
  tableType?: TableType;
  /** CX5 - the stakes pool's villain mix (taggable-four-first). Defaults to the $1/$3 loose-passive roster. */
  roster?: readonly ArchetypeId[];
}

/** Diagnose/Solo play their own preflop (PF4); Observe narrates the GH8 recap over the auto-played one. */
export interface DealOpts {
  preflopPlay?: boolean;
}

/**
 * Deal one hand of the session and walk it. The hand you are dealt is the hand you play.
 *
 * FULL (Joe's call, 2026-07-15) - THE RE-DEAL SCAN IS GONE. It used to re-deal the cards up to 40 times
 * until hero had a postflop decision, so **0% of session hands ended preflop**; without it, measured over
 * 100 hands, **68% do**. That is what poker looks like - a winning full-ring reg folds most hands, and this
 * engine GRADES that fold (`BB_OVERFOLD_VS_BTN` is a real error code, not a non-event). The scan existed
 * because a folded hand taught nothing; TB4 ("the pro reads the table while you're out") ended that, which
 * is exactly the condition its own TODO named for deleting this.
 *
 * It was also the same class of lie as the pre-walk FULL removed from the runner: the table quietly
 * deciding your hand was only worth playing if it reached a flop. Deleting it removes the dominant cost of
 * the old 0.2-3.7s deal freeze too - one walk now, not up to forty.
 *
 * WHAT SURVIVES, and why it is NOT the scan: a bounded salvage for hands the EVALUATOR cannot score
 * (@poker-apprentice `describeHand` throws on certain range x board evaluations). That skips hands that are
 * unusable, never hands that are merely quiet - a preflop fold is a real, graded hand and now stands. The
 * salvage re-deals the CARDS only: button, stacks and roster are the session's and never move, so the table
 * stays continuous either way.
 */
const playSessionHand = (
  initial: HandState,
  baseSeed: string,
  buttonSeat: number,
  startingStacks: readonly number[],
  opts: DealOpts,
): LiveHandModel => {
  for (let k = 0; k < 40; k++) {
    try {
      // k === 0 is the hand the session actually dealt - walk `initial` itself, honoring dealNextHand's
      // contract exactly. k > 0 is the evaluator salvage: same button + stacks, salted cards.
      const state =
        k === 0
          ? initial
          : createHand({
              handId: initial.handId,
              spec: { ...initial.spec, seed: `${baseSeed}#${k}` },
              buttonSeat,
              startingStacks,
            });
      return dealSessionHand(state, opts);
    } catch {
      // unusable hand - deal another. Deterministic: the salvage order is fixed, so the skips reproduce.
      continue;
    }
  }
  throw new Error(`playSessionHand: no evaluable hand for seed '${baseSeed}' in 40 attempts`);
};

/**
 * Re-map every step of a dealt hand through `f`, keeping the model's step arrays consistent with each other.
 *
 * `steps`, `preflops`, `preflop` and `snapshots` share object references by design - `applyHeroDecision`
 * finds the node hero played with `indexOf`, so a re-paint that rebuilt one array and not another would
 * silently record his action at the wrong node (or nowhere). Both re-painters below were open-coding the
 * same rebuild; FULL's preflop-list fix added a third array to keep in step, so it is one helper now.
 */
const mapSteps = (m: LiveHandModel, f: <S extends LiveHandModel['steps'][number]>(s: S) => S): LiveHandModel => {
  const snapshots = m.snapshots.map(f);
  const preflops = m.preflops.map(f);
  return { ...m, snapshots, preflops, preflop: preflops[0], steps: [...preflops, ...snapshots] };
};

/** 'new' = the GH4 cold read (every villain pod shows "?"); 'mid' = the diagnosed ring. Presentation only -
 *  the dealt hand, the advice, and the grade are identical either way. */
const applyMode = (m: LiveHandModel, mode: TableMode): LiveHandModel =>
  mode !== 'new' ? m : mapSteps(m, (s) => ({ ...s, felt: markFeltUnread(s.felt) }));

/**
 * TB3 - paint each seat's PERSISTED read onto every felt of a dealt hand.
 *
 * This is the compounding-reads payoff (TB0 §6.3) made visible, and it is the reason the read lives on the
 * session instead of the surface: a surface is remounted per hand and its `tag` state dies with it, but
 * `session.seats[i].read.userTags` is the same object on hand 9 that you wrote on hand 3 - because it is
 * the same guy in the same seat. Runs AFTER `applyMode`, so a tag you set on a still-"?" seat at a New
 * table survives the cold-read overlay: you have not learned his name, but you HAVE read him, and that is
 * exactly what a real player's mental note looks like.
 *
 * Pure and cheap - it re-maps `SeatView`s, it never re-deals. That is what lets a tag appear the instant
 * you lock it in as well as on every hand after.
 */
export const withReads = (m: LiveHandModel, session: TableSession): LiveHandModel => {
  const tagFor = (seat: number | undefined): string | undefined => {
    if (seat === undefined) return undefined;
    const id = session.seats[seat]?.read.userTags?.[0];
    return id ? ARCHETYPE_TAG[id] : undefined;
  };
  return mapSteps(m, (s) => ({
    ...s,
    felt: { ...s.felt, seats: s.felt.seats.map((seat) => ({ ...seat, tag: tagFor(seat.seat) })) },
  }));
};

/**
 * TB3 - record YOUR read on a seat. PURE (returns a new session), like everything else here.
 *
 * The newest read goes to the FRONT and is what the pod renders, because your latest read is your live one;
 * the two before it are kept (the schema's `max(3)` - North Star §2's "assigning up to three tags and
 * narrowing them as showdowns arrive"), so the history a later revision-tracking session needs is not
 * thrown away here. Re-locking the same tag is idempotent rather than a duplicate entry.
 *
 * NOT touched: `read.revealed` (the GH4 cold-read overlay) and `confirmedTag` (showdown evidence, TB4's
 * lane). Tagging a man is not the same as being proven right about him.
 */
export const tagSeat = (session: TableSession, seat: number, archetypeId: ArchetypeId): TableSession => {
  const target = session.seats[seat];
  if (!target || target.kind !== 'villain') return session; // never tag hero, never invent a seat.
  const prior = target.read.userTags ?? [];
  if (prior[0] === archetypeId) return session;
  const userTags = [archetypeId, ...prior.filter((t) => t !== archetypeId)].slice(0, 3);
  const seats = session.seats.map((s, i) => (i === seat ? { ...s, read: { ...s.read, userTags } } : s));
  return { ...session, seats };
};

/**
 * TB4 - a SHOWDOWN proved something about a seat. Write it down. PURE, like everything else here.
 *
 * This is the `confirmedTag` lane `tagSeat` above deliberately leaves alone ("tagging a man is not the same
 * as being proven right about him"). The two fields answer different questions and must not be conflated:
 * `userTags` is what YOU think, `confirmedTag` is what the table SHOWED - and the gap between them is the
 * whole read-training loop (North Star §2: "assigning up to three tags and narrowing them as showdowns
 * arrive"). Nothing here touches `userTags`: being wrong about a man does not silently rewrite your note,
 * it just means the next beat tells you to re-tag him.
 *
 * Why a showdown is allowed to CONFIRM at all: Axis B is scored against the archetype the scenario engine
 * scripted (North Star §4.1), so the scripted archetype IS the ground truth - a showdown is simply the
 * moment that truth becomes public information. Cards that were never turned over cannot reach this
 * function: its input comes from `walk.shown`, which the engine mints only at a real showdown.
 */
export const confirmSeatReads = (
  session: TableSession,
  evidence: readonly { seat: number; archetypeId: ArchetypeId }[],
): TableSession => {
  if (evidence.length === 0) return session;
  const byId = new Map(evidence.map((e) => [e.seat, e.archetypeId]));
  let changed = false;
  const seats = session.seats.map((s) => {
    const confirmed = byId.get(s.seat);
    if (!confirmed || s.kind !== 'villain' || s.read.confirmedTag === confirmed) return s;
    changed = true;
    return { ...s, read: { ...s.read, confirmedTag: confirmed } };
  });
  return changed ? { ...session, seats } : session;
};

/** TB4 - YOUR current read on each seat (the newest tag, which is the one the pod renders), keyed by seat.
 *  The folded-hand showdown beat needs it to say whether what got turned over confirms or contradicts the
 *  note you actually wrote on that man. */
export const seatTags = (session: TableSession): ReadonlyMap<number, ArchetypeId> => {
  const m = new Map<number, ArchetypeId>();
  for (const s of session.seats) {
    const tag = s.read.userTags?.[0];
    if (tag) m.set(s.seat, tag);
  }
  return m;
};

/**
 * DEAL the session's next hand - and stop there. PURE: `session` is never mutated.
 *
 * FULL: this used to be deal-play-harvest in one call (`dealNextHand`), which is only possible when nobody
 * is waiting to act. Now it deals, walks to a hand whose unplayed decisions carry the PRO's line as a
 * provisional tail, and hands it back for hero to actually play. The chips do not move until `endHand`.
 */
export const nextHand = (session: TableSession, mode: TableMode, opts: DealOpts = {}): TableState => {
  const baseSeed = seedForHand(session);
  const { initial, meta } = beginHand(session);
  const model = playSessionHand(initial, baseSeed, meta.buttonSeat, meta.startingStacks, opts);

  return {
    session,
    // reads are painted from the CURRENT session - `endHand` only moves chips and the button, never reads,
    // so hand N+1 opens with every note you have written on the table either way.
    model: withReads(applyMode(model, mode), session),
    handNumber: meta.handNumber,
    buttonSeat: meta.buttonSeat,
    startingStacks: meta.startingStacks,
    meta,
  };
};

/**
 * FULL - hero really played `decision` at `stepIndex`. Rebuild the hand around it.
 *
 * This is the line that makes the carried stacks yours: `applyHeroDecision` replays the hand from its dealt
 * state with your action recorded, so the board, the villains' responses, and the terminal all move with
 * what you actually did. Re-paints reads + mode so the felt you get back is the felt you were looking at.
 *
 * PURE: returns a NEW TableState. The session is untouched - chips still do not move until `endHand`.
 */
export const playDecision = (
  table: TableState,
  stepIndex: number,
  action: ActionKind,
  amount: string | undefined,
  mode: TableMode,
): TableState => {
  const played = applyHeroDecision(table.model, stepIndex, action, amount);
  return { ...table, model: withReads(applyMode(played, mode), table.session) };
};

/**
 * FULL - the hand is over. HARVEST it into the session: stacks carried, button rotated, reloads applied.
 *
 * This is the moment the table becomes continuous, and it is deliberately the LAST thing that happens to a
 * hand rather than the first. `model.terminal` is real for every decision hero played; if you walked away
 * mid-hand (hit next hand early), the pro's line finished it - which is honest, and is why this never
 * blocks on `model.done`.
 *
 * PURE: returns a NEW session (TB1's `completeHand` does the actual harvest, unchanged).
 */
export const endHand = (table: TableState): { session: TableSession; reloads: SessionEvent[] } => {
  const result = completeHand(table.session, table.meta, table.model.terminal);
  return { session: result.session, reloads: result.reloads };
};

/** Sit down: open the table and deal hand 1. Called ONCE, from App's `sitDown()` - never from inside a
 *  surface, which is the whole point (a surface is remounted and wiped; App is not). */
export const openTable = (opts: OpenTableOptions, deal: DealOpts = {}): TableState => {
  const size = opts.tableSize ?? DEFAULT_TABLE_SIZE;
  const session = newSession({
    sessionId: opts.sessionId ?? `table-${opts.seedRoot}`,
    seedRoot: opts.seedRoot,
    // CX5: the pool's mix drives who sits down; each archetype pulls its own range family + read difficulty,
    // so the $1/$3 vs $3/$5 toughness is real, not a number swap. Defaults to the $1/$3 loose-passive roster.
    roster: rosterFor(size, opts.roster ?? DEFAULT_ROSTER),
    tableMode: opts.mode,
    buyIn: opts.buyIn ?? DEFAULT_BUY_IN,
    stakes: opts.stakes ?? DEFAULT_STAKES,
    tableType: opts.tableType,
    // hero starts on the button, then rotates off it - the first thing a continuous table proves.
    buttonSeat: 0,
  });
  return nextHand(session, opts.mode, deal);
};

/** A fresh seed root - a NEW table (new hands, stacks back to the buy-in), as opposed to a next hand at
 *  the same one. Browser entropy is fine here (the workflow-sandbox Date.now/Math.random ban is a
 *  scripting-sandbox rule, not an SPA one); `?seed=` pins it for screenshots + shares. */
export const freshSeedRoot = (): string =>
  `t-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
